import { Card, CardBody, CardImg, Col, Container, Row, Badge } from 'react-bootstrap';
import Link from 'next/link';
import React, { useEffect, useState, useMemo } from 'react';
import ExcursionsSearchPanel from './ExcursionsSearchPanel';
import bgImg2 from '@/assets/images/bg/02.jpg';
import { currency } from '@/states';
import { excursionsService } from '@/api/services';
import { useExcursionsList } from '@/hooks';
import { useBookPage } from '@/hooks/useBookPage';
import { fetchLowestFromPriceForExcursion } from '@/utils/excursionFromPrice';
import { getBookImage, getExcursionImage, toImageSrc, toPathSegment } from '@/utils/imageUrl';
import { isRuntimeProd } from '@/utils/runtimeEnv';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import offerImg1 from '@/assets/images/offer/01.jpg';
import offerImg2 from '@/assets/images/offer/02.jpg';
import offerImg3 from '@/assets/images/offer/03.jpg';
import offerImg4 from '@/assets/images/offer/04.jpg';
import offerImg5 from '@/assets/images/offer/05.jpg';
import offerImg6 from '@/assets/images/offer/06.jpg';
import offerImg7 from '@/assets/images/offer/07.jpg';
import offerImg8 from '@/assets/images/offer/08.jpg';

const fallbackImages = [offerImg1, offerImg2, offerImg3, offerImg4, offerImg5, offerImg6, offerImg7, offerImg8]
  .map((img) => toImageSrc(img))
  .filter(Boolean);
const MAX_FEATURED_EXCURSIONS = 12;
const ExcursionOverlayCard = ({ excursion, excursionBasePath = '/excursions', priority = false }) => {
  if (!excursion) return null;
  const overlayImageSrc = toImageSrc(excursion.image);

  return (
    <Card className="shadow p-2 pb-0">
      <div className="rounded-3 overflow-hidden position-relative">
        {overlayImageSrc ? (
          <CardImg src={overlayImageSrc} loading={priority ? 'eager' : 'lazy'} />
        ) : (
          <div style={{ height: '180px', background: '#f0f0f0' }} />
        )}
        <div className="bg-overlay bg-dark opacity-3" />
        <div className="card-img-overlay d-flex flex-column">
          {excursion.category && (
            <Badge bg="primary" className="bg-opacity-75 text-white align-self-start">
              {excursion.category}
            </Badge>
          )}
          {excursion.duration && (
            <h6 className="text-white fw-normal mt-auto mb-0">{excursion.duration}</h6>
          )}
        </div>
      </div>
      <CardBody className="px-2">
        <h6 className="card-title mb-2">
          <Link
            href={`${String(excursionBasePath || '/excursions').replace(/\/$/, '')}/${encodeURIComponent(toPathSegment(excursion.slug || excursion.id, ''))}`}
          >
            {excursion.name}
          </Link>
        </h6>
        <div className="d-flex justify-content-between align-items-center mb-0">
          {excursion.price != null ? (
            <h6 className="text-success mb-0">
              {currency}{Number(excursion.price).toFixed(2)} <span className="fw-light">{excursion.pricingUnit === 'group' ? '/group' : '/person'}</span>
            </h6>
          ) : (
            <h6 className="text-muted mb-0">Price on request</h6>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

const Hero = ({ excursionBasePath = '/excursions' }) => {
  const { section, loading: sectionLoading } = useBookPage('excursions_intro');
  const { data: excursionsData } = useExcursionsList();
  const [fromPriceById, setFromPriceById] = useState({});
  const [allPickups, setAllPickups] = useState([]);

  useEffect(() => {
    let cancelled = false;
    excursionsService.getAllPickups()
      .then((res) => {
        if (cancelled) return;
        setAllPickups(res?.data?.data || res?.data || []);
      })
      .catch(() => {
        if (!cancelled) setAllPickups([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const featuredExcursions = useMemo(() => {
    if (!Array.isArray(excursionsData)) {
      return [];
    }

    const isExcursionOnline = (excursion) =>
      !(excursion?.is_online === 0 || excursion?.is_online === "0");

    const transformed = excursionsData
      .filter(isExcursionOnline)
      .map((excursion, idx) => {
      const fallbackImage = fallbackImages[idx % fallbackImages.length];
      let image = null;

      // Prefer main excursion gallery (Media tab) in assigned order.
      if (excursion.images && Array.isArray(excursion.images) && excursion.images.length > 0) {
        const first = excursion.images[0];
        const path = first?.image_path || first?.file_path || first?.file_url || first?.url;
        if (path) {
          image = getExcursionImage(path, null);
        }
      }

      if (!image && excursion.highlights && excursion.highlights.length > 0) {
        const firstHighlight = excursion.highlights[0];
        if (firstHighlight.images && Array.isArray(firstHighlight.images) && firstHighlight.images.length > 0) {
          const imagePath = firstHighlight.images[0];
          image = getExcursionImage(imagePath, null);
        } else if (firstHighlight.media && Array.isArray(firstHighlight.media) && firstHighlight.media.length > 0) {
          const firstMedia = firstHighlight.media[0];
          const imagePath = firstMedia.file_url || firstMedia.image_path || firstMedia.filename || firstMedia.url;
          if (imagePath) {
            image = getExcursionImage(imagePath, null);
          }
        } else if (firstHighlight.image_path) {
          image = getExcursionImage(firstHighlight.image_path, null);
        }
      }

      if (!image) {
        if (excursion.image) {
          image = getExcursionImage(excursion.image, null);
        } else if (excursion.image_path) {
          image = getExcursionImage(excursion.image_path, null);
        } else if (excursion.highlight_image) {
          image = getExcursionImage(excursion.highlight_image, null);
        }
      }

      if (!image) {
        image = isRuntimeProd() ? null : fallbackImage;
      }

      const durationValue = excursion.duration || null;
      let durationLabel = null;
      if (durationValue) {
        if (durationValue < 24) {
          durationLabel = `${durationValue}h`;
        } else {
          const hours = Math.floor(durationValue / 60);
          const mins = durationValue % 60;
          if (hours > 0 && mins > 0) {
            durationLabel = `${hours}h ${mins}m`;
          } else if (hours > 0) {
            durationLabel = `${hours}h`;
          } else {
            durationLabel = `${mins}m`;
          }
        }
      }

      const fromPriceMeta = fromPriceById[String(excursion.id)] || null;
      return {
        id: excursion.id,
        name: excursion.name || 'Excursion',
        slug: excursion.slug,
        image,
        category: excursion.type || excursion.category || 'Excursion',
        price: fromPriceMeta?.price ?? excursion.price ?? excursion.starting_price ?? null,
        pricingUnit: Number(fromPriceMeta?.minimumAdults || 1) > 1 ? 'group' : 'person',
        duration: durationLabel
      };
    });

    return transformed.slice(0, MAX_FEATURED_EXCURSIONS);
  }, [excursionsData, fromPriceById]);

  useEffect(() => {
    if (!Array.isArray(excursionsData) || excursionsData.length === 0) {
      setFromPriceById({});
      return;
    }
    const isExcursionOnline = (excursion) =>
      !(excursion?.is_online === 0 || excursion?.is_online === "0");
    const featuredOnline = excursionsData.filter(isExcursionOnline).slice(0, MAX_FEATURED_EXCURSIONS);
    if (!featuredOnline.length) {
      setFromPriceById({});
      return;
    }
    let cancelled = false;
    Promise.allSettled(
      featuredOnline.map((exc) => fetchLowestFromPriceForExcursion({
        excursion: exc,
        fallbackPickups: allPickups,
        getPricelists: (year, excursionId, regionId) => excursionsService.getPricelists(year, excursionId, regionId),
      }))
    )
      .then((results) => {
        if (cancelled) return;
        const next = {};
        results.forEach((result, idx) => {
          if (result.status !== 'fulfilled' || !result.value) return;
          next[String(featuredOnline[idx].id)] = result.value;
        });
        setFromPriceById(next);
      })
      .catch(() => {
        if (!cancelled) setFromPriceById({});
      });
    return () => {
      cancelled = true;
    };
  }, [excursionsData, allPickups]);

  if (sectionLoading || !section) {
    return null;
  }

  const heroTitle = section.title?.trim() || 'Life Is Adventure Make The Best Of It';
  const heroIntro = section.intro_text?.trim() || 'Planning for a trip? we will organize your best trip with the best destination and within the best budgets!';
  const heroImage = getBookImage(section.image, bgImg2);
  const showSearchForm = section.content?.show_search_form !== false;
  const searchOnly = showSearchForm;
  const showOverlayCards = section.content?.show_overlay_cards !== false;
  const canLoopOverlayCards = featuredExcursions.length > 1;

  return (
    <>
      {!searchOnly && (
      <section className="excursions-top-section bg-mode pt-0 excursions-top-section--photo-hero">
        <div
          className="excursions-hero-bg excursions-hero-bg--photo"
          style={{
            backgroundImage: heroImage
              ? `linear-gradient(135deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.4) 100%), url(${heroImage})`
              : undefined,
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
        >
          <Container>
            <Row className="justify-content-between align-items-center excursions-hero__inner py-5 py-lg-6">
              <Col md={7} lg={8} xxl={7} className="pb-4 pb-md-0 mb-0 mb-lg-0">
                <h1 className="text-white">{heroTitle}</h1>
                <p className="text-white mb-0" dangerouslySetInnerHTML={{ __html: heroIntro }} />
              </Col>
              {showOverlayCards && featuredExcursions.length > 0 && (
                <Col md={5} lg={4} xl={3} className="mb-3 mb-sm-0">
                  <Swiper
                    className="tour-hero-offer-carousel"
                    modules={[Autoplay, EffectFade]}
                    effect="fade"
                    fadeEffect={{ crossFade: true }}
                    loop={canLoopOverlayCards}
                    autoplay={{ delay: 3500, disableOnInteraction: false }}
                    slidesPerView={1}
                  >
                    {featuredExcursions.map((excursion, idx) => (
                      <SwiperSlide key={excursion.id}>
                        <ExcursionOverlayCard excursion={excursion} excursionBasePath={excursionBasePath} priority={idx === 0} />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </Col>
              )}
            </Row>
          </Container>
        </div>
      </section>
      )}
      <ExcursionsSearchPanel
        variant="hero"
        searchOnly={searchOnly}
        showSearchForm={showSearchForm}
      />
    </>
  );
};
export default Hero;